// Google Drive appDataFolder REST client (TASK-VAULT-CLOUD-001).
//
// Runs on the client (browser or Capacitor webview) with a short-lived access
// token minted by our token broker (see lib/google/auth.ts). File bytes go
// straight from the device to Google — they never touch our servers. Everything
// is stored in the hidden, app-scoped `appDataFolder`, so it doesn't clutter
// the user's Drive and isn't visible to other apps. Bytes are expected to be
// already encrypted by the caller (Vault crypto layer).

const DRIVE_FILES_ENDPOINT = 'https://www.googleapis.com/drive/v3/files';
const DRIVE_UPLOAD_ENDPOINT = 'https://www.googleapis.com/upload/drive/v3/files';

const FILE_FIELDS = 'id,name,size,mimeType,modifiedTime,appProperties';

export interface DriveFile {
  id: string;
  name: string;
  size?: string;
  mimeType?: string;
  modifiedTime?: string;
  appProperties?: Record<string, string>;
}

async function driveFetch(token: string, url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    let message = `Drive request failed (${res.status})`;
    try {
      const body = await res.json();
      message = body?.error?.message || message;
    } catch {
      /* non-JSON error body */
    }
    const error = new Error(message) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  return res;
}

/**
 * Uploads bytes to the app's private appDataFolder and returns the file record.
 * `appProperties` lets us stamp our own metadata (e.g. the document id) so we
 * can reconcile Drive files with the Supabase metadata rows.
 */
export async function uploadAppDataFile(opts: {
  token: string;
  name: string;
  data: Blob;
  mimeType?: string;
  appProperties?: Record<string, string>;
}): Promise<DriveFile> {
  const mimeType = opts.mimeType || opts.data.type || 'application/octet-stream';
  const boundary = `crewnest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const metadata = {
    name: opts.name,
    parents: ['appDataFolder'],
    mimeType,
    ...(opts.appProperties ? { appProperties: opts.appProperties } : {}),
  };

  const pre =
    `--${boundary}\r\n` +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    `${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\n` +
    `Content-Type: ${mimeType}\r\n\r\n`;
  const post = `\r\n--${boundary}--`;
  const body = new Blob([pre, opts.data, post], { type: `multipart/related; boundary=${boundary}` });

  const res = await driveFetch(
    opts.token,
    `${DRIVE_UPLOAD_ENDPOINT}?uploadType=multipart&fields=${encodeURIComponent(FILE_FIELDS)}`,
    { method: 'POST', body },
  );
  return (await res.json()) as DriveFile;
}

/** Lists files in the appDataFolder (optionally filtered by a Drive query). */
export async function listAppDataFiles(opts: {
  token: string;
  query?: string;
  pageSize?: number;
}): Promise<DriveFile[]> {
  const files: DriveFile[] = [];
  let pageToken: string | undefined;

  do {
    const params = new URLSearchParams({
      spaces: 'appDataFolder',
      fields: `nextPageToken,files(${FILE_FIELDS})`,
      pageSize: String(opts.pageSize ?? 100),
    });
    if (opts.query) params.set('q', opts.query);
    if (pageToken) params.set('pageToken', pageToken);

    const res = await driveFetch(opts.token, `${DRIVE_FILES_ENDPOINT}?${params.toString()}`);
    const data = (await res.json()) as { files?: DriveFile[]; nextPageToken?: string };
    if (data.files) files.push(...data.files);
    pageToken = data.nextPageToken;
  } while (pageToken);

  return files;
}

/** Downloads a file's raw bytes as a Blob. */
export async function downloadAppDataFile(token: string, fileId: string): Promise<Blob> {
  const res = await driveFetch(token, `${DRIVE_FILES_ENDPOINT}/${encodeURIComponent(fileId)}?alt=media`);
  return res.blob();
}

/** Permanently deletes a file from the appDataFolder. */
export async function deleteAppDataFile(token: string, fileId: string): Promise<void> {
  await driveFetch(token, `${DRIVE_FILES_ENDPOINT}/${encodeURIComponent(fileId)}`, { method: 'DELETE' });
}
